import ExcelJS from "exceljs";
import { saveAs } from "file-saver";

/* ================= HELPERS ================= */

const formatDate = (date) => {
  if (!date) return "";
  return new Date(date).toLocaleDateString("en-GB", {
    day: "2-digit",
    month: "2-digit",
    year: "numeric",
  });
};

const pesoFormat = '"₱"#,##0.00';

const round2 = (n) => Math.round((n + Number.EPSILON) * 100) / 100;

const sanitizeSheetName = (name) =>
  name.replace(/[*?:\\/\[\]]/g, "").substring(0, 31);

/* ================= SUMMARY SHEET ================= */

const createSummarySheet = (workbook, data, cutoffName) => {
  const sheet = workbook.addWorksheet("SOA");

  sheet.mergeCells("A1:C1");
  sheet.getCell("A1").value = "STATEMENT OF ACCOUNT";
  sheet.getCell("A1").font = { bold: true, size: 16 };
  sheet.getCell("A1").alignment = { horizontal: "center" };

  sheet.mergeCells("A2:C2");
  sheet.getCell("A2").value = `Cutoff: ${cutoffName}`;
  sheet.getCell("A2").alignment = { horizontal: "center" };

  sheet.addRow([]);

  const header = sheet.addRow(["Supplier Name", "Payable", "Last Update"]);

  header.font = { bold: true };

  let total = 0;

  data.forEach((item) => {
    // const sheetName = item.supplier_name.substring(0, 31);
    const sheetName = sanitizeSheetName(item.supplier_name);
    const escapedSheetName = sheetName.replace(/'/g, "''");
    total += Number(item.balance || 0);

    const row = sheet.addRow([
      {
        text: item.supplier_name,
        // hyperlink: `#'${sheetName}'!A1`,
        hyperlink: `#'${escapedSheetName}'!A1`,
      },
      Number(item.balance || 0),
      formatDate(item.latest_transaction_date),
    ]);

    row.getCell(1).font = { color: { argb: "FF0000FF" }, underline: true };
    row.getCell(2).numFmt = pesoFormat;
  });

  const totalRow = sheet.addRow(["TOTAL", total, ""]);
  totalRow.font = { bold: true };
  totalRow.getCell(2).numFmt = pesoFormat;

  sheet.columns = [{ width: 35 }, { width: 20 }, { width: 20 }];

  return sheet;
};

/* ================= SUPPLIER SHEET ================= */

const addTransactionsTable = (sheet, transactions = [], finalBalance = 0) => {
  const header = sheet.addRow([
    "Date",
    "Transaction",
    "Charge",
    "Bank",
    "Check #",
    "Check Date",
    "Credit",
    "Balance",
  ]);

  header.font = { bold: true };

  // ✅ STEP 1: sort ASC by transaction date (oldest → newest).
  // Backend sends createdAt DESC; reverse first so same-day entries keep
  // their entry order (sort is stable), then order by the displayed date.
  const sortedTx = [...transactions]
    .reverse()
    .sort((a, b) => new Date(a.date) - new Date(b.date));

  // ✅ STEP 2: compute total movement
  let totalMovement = 0;

  sortedTx.forEach((tx) => {
    totalMovement += Number(tx.charge || 0) - Number(tx.credit || 0);
  });

  // ✅ STEP 3: compute opening balance (normally 0, since the backend balance
  // is the sum of these same transactions)
  let runningBalance = round2(Number(finalBalance || 0) - totalMovement);

  if (runningBalance !== 0) {
    const openingRow = sheet.addRow([
      "",
      "Opening Balance",
      "",
      "",
      "",
      "",
      "",
      runningBalance,
    ]);
    openingRow.getCell(8).numFmt = pesoFormat;
  }

  // ✅ STEP 4: process transactions
  sortedTx.forEach((tx) => {
    const charge = Number(tx.charge || 0);
    const credit = Number(tx.credit || 0);

    runningBalance = round2(runningBalance + charge - credit);

    const row = sheet.addRow([
      formatDate(tx.date),
      tx.transaction || "",
      charge || "",
      tx.bank || "",
      tx.checkNo || "",
      formatDate(tx.checkDate),
      credit || "",
      runningBalance,
    ]);

    row.getCell(3).numFmt = pesoFormat; // Charge
    row.getCell(7).numFmt = pesoFormat; // Credit
    row.getCell(8).numFmt = pesoFormat; // Balance
  });
};

const createSupplierSheet = (workbook, supplier) => {
  // const sheetName = supplier.supplier_name.substring(0, 31);
  const sheetName = sanitizeSheetName(supplier.supplier_name);
  const sheet = workbook.addWorksheet(sheetName);

  // HEADER
  sheet.getCell("A1").value = "STATEMENT OF ACCOUNT";
  sheet.getCell("A1").font = { bold: true, size: 14 };

  sheet.getCell("A3").value = "Supplier:";
  sheet.getCell("B3").value = supplier.supplier_name;

  sheet.getCell("A4").value = "Balance:";
  sheet.getCell("B4").value = Number(supplier.balance || 0);
  sheet.getCell("B4").numFmt = pesoFormat;

  sheet.getCell("A5").value = "Last Update:";
  sheet.getCell("B5").value = formatDate(supplier.latest_transaction_date);

  // BACK LINK
  sheet.getCell("A7").value = {
    text: "⬅ Back to Summary",
    hyperlink: "#'SOA'!A1",
  };
  sheet.getCell("A7").font = {
    color: { argb: "FF0000FF" },
    underline: true,
  };

  sheet.addRow([]);
  sheet.addRow([]);

  // TABLE
  addTransactionsTable(sheet, supplier.transactions, supplier.balance);

  sheet.columns = [
    { width: 15 },
    { width: 20 },
    { width: 15 },
    { width: 30 },
    { width: 15 },
    { width: 15 },
    { width: 15 },
    { width: 18 },
  ];
};

/* ================= MAIN EXPORT ================= */

export const exportToExcelSOASupplier = async (filename, data, cutoffName) => {
  const workbook = new ExcelJS.Workbook();

  // 1. Summary
  createSummarySheet(workbook, data, cutoffName);

  // 2. supplier sheets
  data.forEach((supplier) => {
    createSupplierSheet(workbook, supplier);
  });

  // EXPORT
  const buffer = await workbook.xlsx.writeBuffer();
  saveAs(new Blob([buffer]), `${filename}.xlsx`);
};
